import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Card, CardContent, Grid, IconButton, Divider
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/auth';
import { buildKycMediaUrl } from '../../utils/mediaUrls';

function DocumentLink({ storageKey, label }) {
  const url = buildKycMediaUrl(storageKey);
  if (!url) return null;
  return (
    <Button
      size="small"
      variant="text"
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<FilePresentIcon />}
      sx={{ justifyContent: 'flex-start', p: 0, minWidth: 0, textTransform: 'none', color: '#818cf8' }}
    >
      {label}
    </Button>
  );
}

export default function AdminArbitrators() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Review Dialog State
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [actionType, setActionType] = useState(''); // APPROVE | REJECT
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchProfiles = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/api/v1/kyc/admin/arbitrators/');
      setProfiles(data.arbitrators || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to fetch arbitrator profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const role = localStorage.getItem('cmp_role');
    if (role !== 'ADMIN') {
      if (role === 'ARBITRATOR') {
        navigate('/arbitrations/mine');
      } else if (role === 'BUYER') {
        navigate('/buyer/requirements');
      } else if (role === 'BIDDER') {
        navigate('/bidder/requirements');
      } else {
        navigate('/login');
      }
    } else {
      fetchProfiles();
    }
  }, [navigate]);

  const handleOpenReview = (profile, action) => {
    setSelectedProfile(profile);
    setActionType(action);
    setRemarks('');
  };

  const handleCloseReview = () => {
    setSelectedProfile(null);
    setRemarks('');
    setActionType('');
  };

  const handleReviewSubmit = async () => {
    if (actionType === 'REJECT' && !remarks.trim()) {
      alert('Remarks are required when rejecting.');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/api/v1/kyc/admin/arbitrators/${selectedProfile.id}/review/`, {
        action: actionType,
        remarks: remarks.trim()
      });
      setSuccessMsg(`Successfully ${actionType.toLowerCase()}d ${selectedProfile.full_name}'s application.`);
      fetchProfiles();
      handleCloseReview();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'APPROVED':
        return <Chip label="Approved" color="success" size="small" sx={{ fontWeight: 600 }} />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" sx={{ fontWeight: 600 }} />;
      case 'PENDING':
      default:
        return <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 600 }} />;
    }
  };

  const formatSchedule = (schedule) => {
    if (!schedule || !schedule.working_days || schedule.working_days.length === 0) {
      return 'No schedule set';
    }
    const days = schedule.working_days.map(d => d.substring(0, 3)).join(', ');
    const hours = schedule.start_time && schedule.end_time 
      ? ` (${schedule.start_time} - ${schedule.end_time})`
      : '';
    return `${days}${hours}`;
  };

  const getComputedStatusChip = (status) => {
    switch (status) {
      case 'ONLINE':
        return <Chip label="ONLINE" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#10b981', color: '#10b981', bgcolor: 'rgba(16,185,129,0.05)' }} />;
      case 'BUSY':
        return <Chip label="BUSY" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#f59e0b', color: '#f59e0b', bgcolor: 'rgba(245,158,11,0.05)' }} />;
      default:
        return <Chip label="OFFLINE" size="small" variant="outlined" sx={{ fontWeight: 700, borderColor: '#ef4444', color: '#ef4444', bgcolor: 'rgba(239,68,68,0.05)' }} />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GavelIcon sx={{ fontSize: 32, color: '#818cf8' }} />
          <Typography variant="h4" fontWeight={700} sx={{ color: '#f8fafc' }}>
            Arbitrator Manual Review
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined" color="primary"
            onClick={() => navigate('/admin/deals')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Manage Deals
          </Button>
          <Button
            variant="outlined" color="error" startIcon={<GavelIcon />}
            onClick={() => navigate('/admin/disputes')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Manage Disputes
          </Button>
        </Box>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        You can also review arbitrator KYC documents in Django Admin under{' '}
        <strong>KYC_SVC → Arbitrator profiles</strong> or{' '}
        <strong>Arbitration admin → Arbitrator statuses</strong>{' '}
        (<code>/admin/</code> on the API host). Use the document links to open PDFs/images before approving.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : profiles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', background: '#0e1726', border: '1px solid rgba(129,140,248,0.15)' }}>
          <Typography color="text.secondary">No arbitrator registrations found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{
          background: 'linear-gradient(135deg, rgba(13,21,38,0.95) 0%, rgba(30,41,59,0.95) 100%)',
          border: '1px solid rgba(129,140,248,0.15)',
          borderRadius: 2
        }}>
          <Table aria-label="arbitrators table">
            <TableHead sx={{ bgcolor: 'rgba(15,23,42,0.6)' }}>
              <TableRow>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Arbitrator</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Experience & Specialization</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Schedule & Status</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Workload</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Registrations & Last Login</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Documents</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Approval Status</TableCell>
                <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map((p) => (
                <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'rgba(129,140,248,0.05)' } }}>
                  <TableCell>
                    <Typography fontWeight={600} color="#f1f5f9">{p.full_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{p.email}</Typography>
                    <Typography variant="body2" color="text.secondary">{p.phone || 'No phone'}</Typography>
                    {p.city && <Typography variant="caption" color="text.secondary">{p.city}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#f1f5f9" fontWeight={500}>{p.qualification}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.years_of_experience} yrs exp • {p.specialization.replace(/_/g, ' ')}
                    </Typography>
                    {p.professional_bio && (
                      <Typography variant="body2" color="text.secondary" sx={{
                        mt: 1, maxWidth: 200, whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis'
                      }} title={p.professional_bio}>
                        {p.professional_bio}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ mb: 1 }}>
                      {getComputedStatusChip(p.status)}
                    </Box>
                    <Typography variant="body2" color="#cbd5e1" fontWeight={500}>
                      {formatSchedule(p.working_schedule)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight={700} color={p.active_cases >= 3 ? '#ef4444' : '#cbd5e1'}>
                      {p.active_cases} Active
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {p.bar_council_number && (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" display="block" color="text.secondary">Bar Council:</Typography>
                        <Typography variant="body2" color="#cbd5e1" fontWeight={500}>{p.bar_council_number}</Typography>
                      </Box>
                    )}
                    {p.arbitration_council_number && (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" display="block" color="text.secondary">Arbitration:</Typography>
                        <Typography variant="body2" color="#cbd5e1" fontWeight={500}>{p.arbitration_council_number}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Last Login:</Typography>
                      <Typography variant="body2" color="#94a3b8" sx={{ fontSize: '0.78rem' }}>
                        {p.last_login ? new Date(p.last_login).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <DocumentLink storageKey={p.govt_id_doc_key} label="Govt ID" />
                      <DocumentLink storageKey={p.qualification_doc_key} label="Qualification" />
                      <DocumentLink storageKey={p.certification_doc_key} label="Certification" />
                      {!p.govt_id_doc_key && !p.qualification_doc_key && !p.certification_doc_key && (
                        <Typography variant="caption" color="text.secondary">No documents</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(p.verification_status)}
                    {p.admin_remarks && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, maxWidth: 150 }}>
                        Remarks: {p.admin_remarks}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {p.verification_status === 'PENDING' ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          color="success" size="small"
                          sx={{ border: '1px solid rgba(46,125,50,0.3)', bgcolor: 'rgba(46,125,50,0.05)' }}
                          onClick={() => handleOpenReview(p, 'APPROVE')}
                          title="Approve Arbitrator"
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          color="error" size="small"
                          sx={{ border: '1px solid rgba(211,47,47,0.3)', bgcolor: 'rgba(211,47,47,0.05)' }}
                          onClick={() => handleOpenReview(p, 'REJECT')}
                          title="Reject Arbitrator"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Reviewed</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedProfile} onClose={handleCloseReview} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color={actionType === 'APPROVE' ? 'success' : 'error'} />
          Confirm Application {actionType === 'APPROVE' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to <strong>{actionType.toLowerCase()}</strong> the arbitrator application submitted by <strong>{selectedProfile?.full_name}</strong>?
          </Typography>
          
          <TextField
            label={actionType === 'APPROVE' ? 'Remarks (Optional)' : 'Rejection Reason (Required)'}
            fullWidth
            multiline
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            variant="outlined"
            placeholder={actionType === 'APPROVE' ? 'Add any notes...' : 'Explain why this profile is rejected...'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReview} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color={actionType === 'APPROVE' ? 'success' : 'error'}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : actionType === 'APPROVE' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
